/**
 * Ghost draft rows — the visual echo of an active Scheduling-AI draft on
 * the Day list. Pure builders (no React) so occurrence math is testable.
 *
 * Ghosts are VIEW-ONLY: they carry no appointment id the backend knows,
 * they take no clicks, and they clear when the create rail closes or the
 * draft is discarded. The rail's own flow is the only thing that books.
 *
 * Recurrence stepping (demo approximation, documented): 'Weeks' steps
 * +7 days; 'Months' steps +28 days so every occurrence keeps the parsed
 * weekday (a calendar-month step would drift off it). The rail's real
 * recurrence configurator (POST /api/Appointment/RetrieveSlots) is the
 * authority on actual bookable dates — ghosts only sketch the intent.
 */

import { formatClockTime } from '@/shared/lib/time-format';
import { addMinutesISO } from '@/scheduling/lib/quick-create-slot';
import type { SchedulingCommandDraft } from './scheduling-command-stub-parser';

export interface SchedulingGhostRow {
  /** Synthetic id — namespaced so it can never collide with a real row. */
  id: string;
  /** Local no-Z ISO start of this occurrence. */
  startISO: string;
  endISO: string;
  /** "10:00a - 10:30a" — same shape the day list renders for real rows. */
  timeRange: string;
  /** "Draft — Sarah Khan". */
  label: string;
  /** Profile hint + recurrence summary, when present. */
  sublabel?: string;
  /** True when the backend rejected the desired day (amber variant). */
  rejected: boolean;
}

const DEFAULT_DURATION_MIN = 30;

function fmtTime(d: Date): string {
  return formatClockTime(d);
}

function parseLocalISO(iso: string): Date {
  const [datePart, timePart = '00:00:00'] = iso.split('T');
  const [y, m, day] = datePart.split('-').map(Number);
  const [hh, mm, ss = 0] = timePart.split(':').map(Number);
  return new Date(y, (m ?? 1) - 1, day ?? 1, hh ?? 0, mm ?? 0, ss ?? 0, 0);
}

/**
 * Expand an active draft into its ghost occurrences.
 *
 * @param draft    the parsed command draft (needs `start` to render).
 * @param rejected backend-rejection flag — paints every occurrence amber.
 */
export function buildGhostRows(
  draft: SchedulingCommandDraft,
  rejected = false,
): SchedulingGhostRow[] {
  if (!draft.start) return [];
  const duration = draft.durationMinutes ?? DEFAULT_DURATION_MIN;
  const count = draft.recurrence ? Math.max(1, draft.recurrence.count) : 1;
  const stepDays = draft.recurrence?.period === 'Months' ? 28 : 7;

  const sublabelParts: string[] = [];
  if (draft.profileQuery) sublabelParts.push(draft.profileQuery);
  if (draft.doctorQuery) sublabelParts.push(`with ${draft.doctorQuery}`);
  if (draft.recurrence) {
    sublabelParts.push(
      `${draft.recurrence.period === 'Months' ? 'Monthly' : 'Weekly'} · ${count} occurrence${count === 1 ? '' : 's'}`,
    );
  }
  const sublabel = sublabelParts.length > 0 ? sublabelParts.join(' · ') : undefined;

  const rows: SchedulingGhostRow[] = [];
  for (let n = 0; n < count; n++) {
    const startISO = addMinutesISO(draft.start, n * stepDays * 24 * 60);
    const endISO = addMinutesISO(startISO, duration);
    rows.push({
      id: `scheduling-ai-ghost-${n}-${startISO}`,
      startISO,
      endISO,
      timeRange: `${fmtTime(parseLocalISO(startISO))} - ${fmtTime(parseLocalISO(endISO))}`,
      label: `Draft — ${draft.patientQuery}`,
      ...(sublabel ? { sublabel } : {}),
      rejected,
    });

    // Back-to-back chain (shape C): each later segment starts when the
    // previous visit ends — the receptionist's multi-provider flow. The
    // chain repeats inside every recurrence occurrence.
    let chainStart = endISO;
    (draft.chain ?? []).forEach((seg, c) => {
      const segDuration = seg.durationMinutes ?? duration;
      const segEnd = addMinutesISO(chainStart, segDuration);
      const segParts: string[] = [];
      if (seg.profileQuery) segParts.push(seg.profileQuery);
      if (seg.doctorQuery) segParts.push(`with ${seg.doctorQuery}`);
      segParts.push('back-to-back');
      rows.push({
        id: `scheduling-ai-ghost-${n}c${c}-${chainStart}`,
        startISO: chainStart,
        endISO: segEnd,
        timeRange: `${fmtTime(parseLocalISO(chainStart))} - ${fmtTime(parseLocalISO(segEnd))}`,
        label: `Draft — ${draft.patientQuery}`,
        sublabel: segParts.join(' · '),
        rejected,
      });
      chainStart = segEnd;
    });
  }
  return rows;
}

/** Filter ghost occurrences to the day the calendar is showing. */
export function ghostRowsForDay(
  ghosts: SchedulingGhostRow[],
  selectedDate: Date,
): SchedulingGhostRow[] {
  return ghosts.filter((g) => {
    const d = parseLocalISO(g.startISO);
    return (
      d.getFullYear() === selectedDate.getFullYear() &&
      d.getMonth() === selectedDate.getMonth() &&
      d.getDate() === selectedDate.getDate()
    );
  });
}
